import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './product-variants.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    KontentMedia,
    KontentRichText,
    KontentStringValue,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import { useIsClient } from '../Global/DataUtils/useIsClient';
import {
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';

/** Size of the CDN-resized asset; falls back when Kontent dims are missing. */
const deliveredSize = (
    image: { width: number; height: number } | null,
    targetWidth: number,
    fallbackHeight: number
) =>
    image?.width && image?.height
        ? getDisplayDimensions(image.width, image.height, targetWidth)
        : { width: targetWidth, height: fallbackHeight };

type Variant = {
    elements: {
        description: KontentRichText;
        headline: KontentStringValue;
        subheadline: KontentStringValue;
        primary_image: KontentMedia;
        secondary_image: KontentMedia;
    };
};

interface Props {
    headline: KontentStringValue;
    short_description: KontentRichText;
    variants: {
        value: Variant[];
    };
    kontentItemId?: string;
}

const ProductVariants: React.FC<Props> = ({
    headline,
    short_description,
    kontentItemId,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const isClient = useIsClient();
    const productVariants = props.variants.value;
    const isMediumScreen = isClient && breakpoints.md;
    const currentDisplayNumber = isMediumScreen ? 4 : 1;
    const [visibleVariants, setVisibleVariants] = useState(
        productVariants.slice(0, currentDisplayNumber)
    );
    const [currentVariant, setCurrentVariant] = useState(0);
    const numVariants = productVariants.length;
    const strokeColor = '#505251';
    const showArrows = numVariants > currentDisplayNumber;

    useEffect(() => {
        setVisibleVariants(productVariants.slice(0, isMediumScreen ? 4 : 1));
    }, [isMediumScreen]);

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newVariant = currentVariant + 1;

        let newVisibleVariants = productVariants.slice(
            newVariant,
            newVariant + currentDisplayNumber
        );

        setVisibleVariants(newVisibleVariants);
        setCurrentVariant(newVariant);
    };

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newVariant = currentVariant - 1;

        let newVisibleVariants = productVariants.slice(
            newVariant,
            newVariant + currentDisplayNumber
        );

        setCurrentVariant(newVariant);
        setVisibleVariants(newVisibleVariants);
    };

    const renderVariant = (variant: Variant, idx: number) => {
        if (isMediumScreen || idx === 0) {
            const topImage = getImageData(variant.elements.primary_image);
            const bottomImage = getImageData(variant.elements.secondary_image);

            return (
                <div className={styles.variant} key={idx}>
                    {topImage.image && (
                        <div data-kontent-element-codename="primary_image">
                            <LazyImage
                                src={
                                    topImage.image
                                        ? optimizeKontentImageUrl(
                                              (topImage.image?.url ||
                                                  topImage.image) as string,
                                              {
                                                  w: 327,
                                                  fit: 'crop',
                                                  q: 80,
                                                  auto: 'format',
                                              }
                                          )
                                        : ''
                                }
                                srcSet={
                                    topImage.image
                                        ? buildKontentSrcSet(
                                              (topImage.image?.url ||
                                                  topImage.image) as string,
                                              327,
                                              {
                                                  fit: 'crop',
                                                  q: 80,
                                                  auto: 'format',
                                              }
                                          )
                                        : ''
                                }
                                sizes="(max-width: 327px) 100vw, 327px"
                                alt={topImage.alt}
                                {...deliveredSize(topImage.image, 327, 273)}
                                className={styles.topImageContainer}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: 'cover',
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.borderContainer}>
                        {bottomImage.image && (
                            <div data-kontent-element-codename="secondary_image">
                                <LazyImage
                                    src={
                                        bottomImage.image
                                            ? optimizeKontentImageUrl(
                                                  (bottomImage.image?.url ||
                                                      bottomImage.image) as string,
                                                  {
                                                      w: 250,
                                                      fit: 'crop',
                                                      q: 80,
                                                      auto: 'format',
                                                  }
                                              )
                                            : ''
                                    }
                                    srcSet={
                                        bottomImage.image
                                            ? buildKontentSrcSet(
                                                  (bottomImage.image?.url ||
                                                      bottomImage.image) as string,
                                                  250,
                                                  {
                                                      fit: 'crop',
                                                      q: 80,
                                                      auto: 'format',
                                                  }
                                              )
                                            : ''
                                    }
                                    sizes="(max-width: 250px) 100vw, 250px"
                                    alt={bottomImage.alt}
                                    {...deliveredSize(
                                        bottomImage.image,
                                        250,
                                        262
                                    )}
                                    className={styles.bottomImageContainer}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'cover',
                                    }}
                                />
                            </div>
                        )}
                        <h3
                            className={styles.variantTitle}
                            data-kontent-element-codename="headline"
                        >
                            {variant.elements.headline.value}
                        </h3>
                        <p
                            className={styles.variantSubheading}
                            data-kontent-element-codename="subheadline"
                        >
                            {variant.elements.subheadline.value}
                        </p>
                        <div className={styles.variantDescription}>
                            {RichText(
                                variant.elements.description,
                                undefined,
                                undefined,
                                undefined,
                                'description'
                            )}
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <section
            className={styles.wrapper}
            data-kontent-item-id={kontentItemId}
        >
            <h2
                className={styles.title}
                data-kontent-element-codename="headline"
            >
                {headline.value}
            </h2>
            <div className={styles.description}>
                {RichText(
                    short_description,
                    false,
                    undefined,
                    undefined,
                    'short_description'
                )}
            </div>
            <div
                className={styles.carouselContainer}
                style={
                    visibleVariants.length <= 3 && isMediumScreen
                        ? { display: 'flex', justifyContent: 'center' }
                        : {}
                }
            >
                <div
                    className={styles.variantsContainer}
                    style={
                        visibleVariants.length <= 3 && isMediumScreen
                            ? {
                                  gridTemplateColumns: `repeat(${
                                      visibleVariants.length
                                  }, minmax(${
                                      visibleVariants.length == 3
                                          ? '220px'
                                          : '200px'
                                  }, 400px))`,
                                  textAlign: 'center',
                              }
                            : {}
                    }
                >
                    {visibleVariants.map((variant, idx) =>
                        renderVariant(variant, idx)
                    )}
                </div>
                {showArrows && currentVariant != 0 ? (
                    <ArrowLgButton
                        direction="left"
                        handleClick={handleClickPrev}
                        strokeColor={strokeColor}
                        className={styles.prev}
                    />
                ) : null}
                {showArrows &&
                currentVariant !=
                    productVariants.length - currentDisplayNumber ? (
                    <ArrowLgButton
                        direction="right"
                        handleClick={handleClickNext}
                        strokeColor={strokeColor}
                        className={styles.next}
                    />
                ) : null}
            </div>
        </section>
    );
};

export default ProductVariants;

export const fragmentProductVariants = graphql`
    fragment ProductVariants on kontent_item_component___product_variants {
        id
        elements {
            headline {
                value
            }
            short_description {
                ...kontentRichText
            }
            variants {
                value {
                    ... on kontent_item_content_item___variant {
                        id
                        elements {
                            description {
                                ...kontentRichText
                            }
                            headline {
                                value
                            }
                            primary_image {
                                ...kontentImage
                            }
                            secondary_image {
                                ...kontentImage
                            }
                            subheadline {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
